import {
  Avatar,
  Box,
  Text,
  VStack,
  HStack,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
  Switch,
  Card,
  CardBody,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Select,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  SimpleGrid,
  Tooltip,
  Icon,
  Divider,
  Progress,
  Tag,
  TagLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react'
import { SearchIcon, CloseIcon } from '@chakra-ui/icons'
import {
  LuUsers,
  LuShield,
  LuRefreshCw,
  LuFilter,
  LuEye,
  LuMicroscope,
  LuVideo,
  LuBookOpen,
  LuShieldCheck,
  LuStethoscope,
  LuTrash2,
  LuUserCheck,
  LuBuilding2,
  LuActivity,
  LuUserPlus,
  LuMegaphone,
  LuDownload,
} from 'react-icons/lu'
import { useState, useEffect, useMemo, useRef } from 'react'
import axiosClient from '../../api/axios'
import { LAB_APPS, isLabAppEnabled, withLabAppPermission } from '../../config/labApps'
import InvitationsAdmin from './InvitationsAdmin'
import CampaignsAdmin from './CampaignsAdmin'
import CaseVisitorsAdmin from './CaseVisitorsAdmin'
import ExportAdmin from './ExportAdmin'
import { PilotsDashboard } from './PilotsAdmin'
import ChuActivity from '../../components/admin/ChuActivity'
import BlotFlowAudit from '../../components/admin/BlotFlowAudit'
import { planColor, planLabel } from '../../config/plans'
import { format, formatDistanceToNowStrict } from 'date-fns'
import { fr } from 'date-fns/locale'
import { CHROME_HEADER_GRADIENT, CHROME_GLASS_BORDER } from '../../theme/chrome'

/** dd/MM/yyyy — the admin panel reads dates at a glance, not timestamps. */
const formatDate = (iso: string) => format(new Date(iso), 'dd/MM/yyyy')
/** "il y a 3 jours" / "dans 13 jours" — signed, so it works for past and future. */
const relativeDays = (iso: string) => {
  const d = new Date(iso)
  const dist = formatDistanceToNowStrict(d, { locale: fr })
  return d.getTime() < Date.now() ? `il y a ${dist}` : `dans ${dist}`
}

interface User {
  id: string
  email: string
  contactName: string
  companyName?: string
  role: string
  plan?: string
  modules?: UserModule[]
  subscription?: { plan: string; status: string }
  createdAt?: string
  creditBalance?: {
    remainingCredits: number
    totalCredits: number
    usedCredits?: number
    periodStartDate?: string
    periodEndDate?: string
  } | null
  /** Usage signal surfaced in the details modal (from /users/all). */
  activity?: { blotCases: number; lastCaseAt: string | null }
}

// Effective plan for a user: prefer the active subscription, fall back to the
// User.plan column, then "free". Keeps the admin list in sync with what the app
// actually grants (and with the invitations plan tier).
const effectivePlan = (u: User): string => u.subscription?.plan || u.plan || 'free'

interface UserModule {
  id: string
  moduleType: string
  isEnabled: boolean
  permissions: string[]
}


interface ModuleConfig {
  key: string
  label: string
  icon: React.ComponentType
  description: string
  colorScheme: string
  selectable: boolean
  note?: string
}

const MODULE_CONFIGS: ModuleConfig[] = [
  {
    key: 'teleexpertise',
    label: 'Téléexpertise',
    icon: LuVideo,
    description: 'Collaboration et consultation à distance entre experts',
    colorScheme: 'blue',
    selectable: true,
  },
  {
    key: 'lab',
    label: 'Lab Studio',
    icon: LuMicroscope,
    description: 'Applications de diagnostic médical assistées par IA',
    colorScheme: 'green',
    selectable: true,
  },
  {
    key: 'literature_watch',
    label: 'MedInsight',
    icon: LuBookOpen,
    description: 'Hub de recherche documentaire médicale avec résumés IA',
    colorScheme: 'purple',
    selectable: true,
    note: 'ex. Literature Watch',
  },
  {
    key: 'patients',
    label: 'Patients',
    icon: LuStethoscope,
    description: 'Inclus automatiquement avec Téléexpertise et Lab',
    colorScheme: 'teal',
    selectable: false,
  },
  {
    key: 'admin',
    label: 'Admin',
    icon: LuShieldCheck,
    description: 'Paramètres administratifs',
    colorScheme: 'red',
    selectable: false,
  },
]

const SELECTABLE_MODULES = MODULE_CONFIGS.filter((m) => m.selectable)

/**
 * Les colonnes à interrupteur : les modules, puis les applications du Lab
 * Studio lues dans le registre partagé (src/config/labApps.ts).
 *
 * Une application ajoutée là-bas apparaît ici d'elle-même, avec son
 * interrupteur. C'est tout l'objet du registre : il n'y a plus de liste à
 * tenir à jour en double, et le panneau ne peut plus proposer un module qui
 * n'existe pas — « Training » n'a ni page ni route depuis longtemps.
 */
interface ToggleColumn {
  key: string
  label: string
  icon: React.ComponentType
  description: string
  colorScheme: string
  kind: 'module' | 'labApp'
}

const TOGGLE_COLUMNS: ToggleColumn[] = [
  ...SELECTABLE_MODULES.map((m) => ({
    key: m.key, label: m.label, icon: m.icon,
    description: m.description, colorScheme: m.colorScheme, kind: 'module' as const,
  })),
  ...LAB_APPS.map((a) => ({
    key: a.id, label: a.label, icon: a.icon as React.ComponentType,
    description: `Lab Studio — ${a.description}`, colorScheme: a.colorScheme, kind: 'labApp' as const,
  })),
]

const roleConfig: Record<string, { label: string; colorScheme: string }> = {
  admin: { label: 'Admin', colorScheme: 'purple' },
  super_admin: { label: 'Super Admin', colorScheme: 'red' },
  user: { label: 'Utilisateur', colorScheme: 'gray' },
}

// planConfig now lives in src/config/plans.ts — see top import.

function UserAvatar({ name, email }: { name?: string; email: string }) {
  const initials = name
    ? name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()
    : email[0].toUpperCase()
  return (
    <Avatar size="sm" name={name || email} bg="purple.500" color="white" />
  )
}

function StatCard({
  label, value, icon, colorScheme,
}: { label: string; value: number; icon: React.ComponentType; colorScheme: string }) {
  return (
    <Card overflow="hidden" borderWidth="1px" borderColor={`${colorScheme}.100`}>
      <Box h="3px" bgGradient={`linear(to-r, ${colorScheme}.400, ${colorScheme}.600)`} />
      <CardBody>
        <HStack justify="space-between" align="flex-start">
          <VStack align="start" spacing={0}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
              {label}
            </Text>
            <Text fontSize="3xl" fontWeight="bold" color={`${colorScheme}.600`} lineHeight="1.2" mt={1}>
              {value}
            </Text>
          </VStack>
          <Box
            p={2.5}
            borderRadius="lg"
            bg={`${colorScheme}.50`}
            border="1px solid"
            borderColor={`${colorScheme}.100`}
          >
            <Icon as={icon} boxSize={5} color={`${colorScheme}.500`} />
          </Box>
        </HStack>
      </CardBody>
    </Card>
  )
}

interface UserDetailsModalProps {
  user: User | null
  isOpen: boolean
  onClose: () => void
  /** Éteint un module hérité — ceux que le catalogue ne connaît plus. */
  onDisableModule?: (userId: string, moduleType: string) => void
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ user, isOpen, onClose, onDisableModule }) => {
  if (!user) return null
  const enabledModules = user.modules?.filter((m) => m.isEnabled) || []
  const plan = effectivePlan(user)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl" overflow="hidden">
        {/* Gradient header */}
        <Box bgGradient={CHROME_HEADER_GRADIENT} px={6} py={5}>
          <HStack spacing={4}>
            <Avatar
              size="lg"
              name={user.contactName || user.email}
              bg="whiteAlpha.300"
              color="white"
              border="2px solid"
              borderColor="whiteAlpha.400"
            />
            <VStack align="start" spacing={0.5}>
              <Text fontWeight="bold" color="white" fontSize="lg">{user.contactName || 'Sans nom'}</Text>
              <Text fontSize="sm" color="whiteAlpha.800">{user.email}</Text>
              {user.companyName && (
                <HStack spacing={1} mt={0.5}>
                  <Icon as={LuBuilding2} color="whiteAlpha.700" boxSize={3} />
                  <Text fontSize="xs" color="whiteAlpha.700">{user.companyName}</Text>
                </HStack>
              )}
            </VStack>
          </HStack>
        </Box>
        <ModalCloseButton color="white" top={3} />

        <ModalBody pb={6} pt={5}>
          <VStack spacing={5} align="stretch">
            {/* Role + Plan */}
            <SimpleGrid columns={2} spacing={3}>
              <Box bg="gray.50" borderRadius="lg" p={3}>
                <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Rôle</Text>
                <Badge
                  colorScheme={roleConfig[user.role]?.colorScheme || 'gray'}
                  variant="solid"
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  {roleConfig[user.role]?.label || user.role}
                </Badge>
              </Box>
              <Box bg="gray.50" borderRadius="lg" p={3}>
                <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Formule</Text>
                <Badge
                  colorScheme={planColor(plan)}
                  variant="solid"
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  {planLabel(plan)}
                </Badge>
              </Box>
            </SimpleGrid>

            {/* Credits */}
            {user.creditBalance && (
              <Box bg="purple.50" borderRadius="lg" p={4} border="1px solid" borderColor="purple.100">
                <Text fontSize="xs" color="purple.600" fontWeight="semibold" mb={2} textTransform="uppercase" letterSpacing="wide">
                  Crédits IA
                </Text>
                <HStack justify="space-between">
                  <Text fontSize="2xl" fontWeight="bold" color="purple.700">
                    {user.creditBalance.remainingCredits === 999999 ? '∞' : user.creditBalance.remainingCredits}
                  </Text>
                  {user.creditBalance.totalCredits !== 999999 && (
                    <Text fontSize="sm" color="gray.500">/ {user.creditBalance.totalCredits} total</Text>
                  )}
                </HStack>
              </Box>
            )}

            <Divider />

            {/* Active modules */}
            <Box>
              <HStack justify="space-between" mb={3}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                  Modules actifs
                </Text>
                <Badge colorScheme={enabledModules.length > 0 ? 'green' : 'gray'} borderRadius="full">
                  {enabledModules.length}
                </Badge>
              </HStack>
              {enabledModules.length > 0 ? (
                <SimpleGrid columns={2} spacing={2}>
                  {enabledModules.map((module) => {
                    const config = MODULE_CONFIGS.find((m) => m.key === module.moduleType)
                    // UN MODULE QUE LE CATALOGUE NE CONNAÎT PLUS EST HÉRITÉ.
                    //
                    // `training` et `collaboratory` vivent encore dans l'ENUM de la
                    // base, mais n'ont plus ni page ni route : les activer envoie
                    // l'utilisateur sur un 404. Ils n'apparaissaient nulle part dans
                    // ce panneau, donc personne ne pouvait les éteindre. On les
                    // montre désormais pour ce qu'ils sont, avec de quoi les couper.
                    const legacy = !config
                    return (
                      <HStack
                        key={module.moduleType}
                        bg={legacy ? 'orange.50' : `${config?.colorScheme || 'gray'}.50`}
                        borderRadius="lg"
                        p={2.5}
                        border="1px solid"
                        borderColor={legacy ? 'orange.200' : `${config?.colorScheme || 'gray'}.200`}
                        spacing={2}
                      >
                        <Icon
                          as={config?.icon || LuShield}
                          boxSize={4}
                          color={legacy ? 'orange.500' : `${config?.colorScheme || 'gray'}.600`}
                        />
                        <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                          {config?.label || module.moduleType}
                        </Text>
                        {legacy && (
                          <>
                            <Badge colorScheme="orange" fontSize="2xs" borderRadius="full">
                              hérité
                            </Badge>
                            <Tooltip
                              label="Ce module n’a plus de page : l’activer mène à une page introuvable. Cliquez pour le désactiver."
                              hasArrow
                              placement="top"
                            >
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="orange"
                                onClick={() => onDisableModule?.(user.id, module.moduleType)}
                              >
                                Désactiver
                              </Button>
                            </Tooltip>
                          </>
                        )}
                      </HStack>
                    )
                  })}
                </SimpleGrid>
              ) : (
                <Box bg="gray.50" borderRadius="lg" p={4} textAlign="center">
                  <Text fontSize="sm" color="gray.400">Aucun module activé</Text>
                </Box>
              )}
            </Box>

            <Divider />

            {/* Account + usage — the questions actually asked about a pilot:
                since when, how much have they used, and are they about to run
                out of credits mid-session. */}
            <Box>
              <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
                Compte et activité
              </Text>
              <SimpleGrid columns={2} spacing={3}>
                <Box bg="gray.50" borderRadius="lg" p={3}>
                  <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Inscrit le</Text>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                    {user.createdAt ? formatDate(user.createdAt) : '—'}
                  </Text>
                  {user.createdAt && (
                    <Text fontSize="2xs" color="gray.400">{relativeDays(user.createdAt)}</Text>
                  )}
                </Box>

                <Box bg="gray.50" borderRadius="lg" p={3}>
                  <Text fontSize="xs" color="gray.500" mb={1} fontWeight="medium">Analyses blot</Text>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                    {user.activity?.blotCases ?? 0}
                  </Text>
                  <Text fontSize="2xs" color="gray.400">
                    {user.activity?.lastCaseAt
                      ? `dernière : ${relativeDays(user.activity.lastCaseAt)}`
                      : 'aucune analyse'}
                  </Text>
                </Box>
              </SimpleGrid>

              {/* Credits — highlighted when the balance is running low, since that
                  silently blocks a pilot mid-session. */}
              {user.creditBalance ? (() => {
                const { remainingCredits: left, totalCredits: total, periodEndDate } = user.creditBalance!
                const ratio = total > 0 ? left / total : 0
                const tone = ratio <= 0.15 ? 'red' : ratio <= 0.35 ? 'orange' : 'green'
                return (
                  <Box mt={3} bg={`${tone}.50`} border="1px solid" borderColor={`${tone}.200`} borderRadius="lg" p={3}>
                    <HStack justify="space-between" mb={2}>
                      <Text fontSize="xs" color="gray.600" fontWeight="medium">Crédits</Text>
                      <Badge colorScheme={tone} borderRadius="full" fontSize="2xs">
                        {left} / {total} restants
                      </Badge>
                    </HStack>
                    <Progress
                      value={total > 0 ? (left / total) * 100 : 0}
                      size="sm"
                      colorScheme={tone}
                      borderRadius="full"
                      bg={`${tone}.100`}
                    />
                    {periodEndDate && (
                      <Text fontSize="2xs" color="gray.500" mt={1.5}>
                        Renouvellement le {formatDate(periodEndDate)} ({relativeDays(periodEndDate)})
                      </Text>
                    )}
                  </Box>
                )
              })() : (
                <Box mt={3} bg="gray.50" borderRadius="lg" p={3}>
                  <Text fontSize="xs" color="gray.400">Aucun solde de crédits</Text>
                </Box>
              )}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default function UserModuleAdmin() {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [planFilter, setPlanFilter] = useState<string>('all')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [userToDelete, setUserToDelete] = useState<User | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const toast = useToast()

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await axiosClient.get('/users/all?page=1&limit=1000')
      setUsers(response.data.users || response.data)
      setError(null)
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Impossible de charger les utilisateurs')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let result = users
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (u) =>
          u.contactName?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.companyName?.toLowerCase().includes(q)
      )
    }
    if (roleFilter !== 'all') result = result.filter((u) => u.role === roleFilter)
    if (planFilter !== 'all') result = result.filter((u) => effectivePlan(u) === planFilter)
    setFilteredUsers(result)
  }, [users, search, roleFilter, planFilter])

  const toggleModule = async (userId: string, moduleType: string, isEnabled: boolean) => {
    try {
      await axiosClient.put(`/users/${userId}/modules/${moduleType}`, {
        isEnabled: !isEnabled,
        permissions: isEnabled ? [] : ['read', 'write'],
      })
      toast({
        title: `Module ${!isEnabled ? 'activé' : 'désactivé'}`,
        description: MODULE_CONFIGS.find((m) => m.key === moduleType)?.label || moduleType,
        status: 'success',
        duration: 2000,
        isClosable: true,
      })
      fetchUsers()
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.message || 'Échec de la mise à jour',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const hasModule = (user: User, moduleType: string) =>
    user.modules?.some((m) => m.moduleType === moduleType && m.isEnabled) || false

  const labPermissionsOf = (user: User) =>
    user.modules?.find((m) => m.moduleType === 'lab')?.permissions

  // Une application du Lab n'est ouverte que si le Lab Studio l'est aussi :
  // l'interrupteur ne promet rien que l'utilisateur ne puisse atteindre.
  const isColumnActive = (user: User, col: ToggleColumn) =>
    col.kind === 'module'
      ? hasModule(user, col.key)
      : hasModule(user, 'lab') && isLabAppEnabled(labPermissionsOf(user), col.key)

  /**
   * Ouvre ou ferme une application du Lab pour cet utilisateur.
   *
   * Elle se range dans les `permissions` du module `lab` et non dans un type
   * de module à elle : `moduleType` est un ENUM Postgres, et lui ajouter une
   * valeur demanderait une migration. Ouvrir une application allume le Lab
   * Studio au passage, sans quoi l'interrupteur serait sans effet visible.
   */
  const toggleLabApp = async (user: User, appId: string, active: boolean) => {
    const label = LAB_APPS.find((a) => a.id === appId)?.label || appId
    try {
      await axiosClient.put(`/users/${user.id}/modules/lab`, {
        isEnabled: active ? hasModule(user, 'lab') : true,
        permissions: withLabAppPermission(labPermissionsOf(user), appId, !active),
      })
      toast({
        title: `${label} ${!active ? 'activé' : 'désactivé'}`,
        description: 'Application du Lab Studio',
        status: 'success',
        duration: 2000,
        isClosable: true,
      })
      fetchUsers()
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.message || 'Échec de la mise à jour',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return
    try {
      setIsDeleting(true)
      await axiosClient.delete(`/users/${userToDelete.id}`)
      toast({
        title: 'Utilisateur supprimé',
        description: `${userToDelete.contactName || userToDelete.email} supprimé avec succès`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      fetchUsers()
    } catch (err: any) {
      toast({
        title: 'Erreur',
        description: err.response?.data?.message || 'Échec de la suppression',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsDeleting(false)
      setUserToDelete(null)
      onDeleteClose()
    }
  }

  useEffect(() => { fetchUsers() }, [])

  const stats = useMemo(() => ({
    total: users.length,
    admins: users.filter((u) => u.role === 'admin' || u.role === 'super_admin').length,
    pro: users.filter((u) => effectivePlan(u) === 'pro').length,
    premium: users.filter((u) => effectivePlan(u) === 'premium').length,
    enterprise: users.filter((u) => effectivePlan(u) === 'enterprise').length,
  }), [users])

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="60vh" direction="column" gap={4}>
        <Spinner size="xl" color="purple.500" thickness="4px" speed="0.8s" />
        <Text color="gray.500" fontSize="sm">Chargement des utilisateurs...</Text>
      </Flex>
    )
  }

  if (error) {
    return (
      <Box p={8}>
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          <VStack align="start" spacing={1}>
            <Text fontWeight="bold">Erreur de chargement</Text>
            <Text fontSize="sm">{error}</Text>
            <Button size="sm" colorScheme="red" mt={1} onClick={fetchUsers} leftIcon={<Icon as={LuRefreshCw} />}>
              Réessayer
            </Button>
          </VStack>
        </Alert>
      </Box>
    )
  }

  return (
    <Box p={{ base: 4, md: 6 }} maxW="1600px" mx="auto">
      <VStack spacing={5} align="stretch">

        {/* Header */}
        <Flex
          align="center"
          justify="space-between"
          bgGradient={CHROME_HEADER_GRADIENT}
          borderRadius="2xl"
          p={6}
          position="relative"
          overflow="hidden"
          boxShadow="0 4px 24px rgba(15, 10, 26, 0.4)"
          sx={{ '&::after': CHROME_GLASS_BORDER }}
        >
          <HStack spacing={4}>
            <Box
              w={12} h={12}
              borderRadius="xl"
              bg="whiteAlpha.200"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="1px solid"
              borderColor="whiteAlpha.300"
            >
              <Icon as={LuShield} boxSize={6} color="white" />
            </Box>
            <VStack align="start" spacing={0}>
              <Heading size="md" color="white">Panneau d'Administration</Heading>
              <Text fontSize="sm" color="whiteAlpha.800">
                Gestion des utilisateurs et des modules
              </Text>
            </VStack>
          </HStack>
          <Button
            onClick={fetchUsers}
            leftIcon={<Icon as={LuRefreshCw} />}
            bg="whiteAlpha.200"
            color="white"
            _hover={{ bg: 'whiteAlpha.300' }}
            size="sm"
            borderRadius="lg"
            border="1px solid"
            borderColor="whiteAlpha.300"
          >
            Actualiser
          </Button>
        </Flex>

        {/* Stats */}
        <SimpleGrid columns={{ base: 2, lg: 5 }} spacing={4}>
          <StatCard label="Utilisateurs" value={stats.total} icon={LuUsers} colorScheme="purple" />
          <StatCard label="Administrateurs" value={stats.admins} icon={LuShield} colorScheme="red" />
          <StatCard label="Essentiel" value={stats.pro} icon={LuUserCheck} colorScheme="teal" />
          <StatCard label="Premium" value={stats.premium} icon={LuUserCheck} colorScheme="orange" />
          <StatCard label="Institution" value={stats.enterprise} icon={LuBuilding2} colorScheme="purple" />
        </SimpleGrid>

        {/* ── Tabs: existing user/module table + new pilot follow-up dashboard ── */}
        <Tabs colorScheme="purple" variant="enclosed">
          <TabList>
            <Tab fontWeight="600" fontSize="sm">
              <HStack spacing={2}>
                <Icon as={LuUsers} boxSize={4} />
                <Text>Utilisateurs & modules</Text>
              </HStack>
            </Tab>
            <Tab fontWeight="600" fontSize="sm">
              <HStack spacing={2}>
                <Icon as={LuActivity} boxSize={4} />
                <Text>Activité</Text>
              </HStack>
            </Tab>
            <Tab fontWeight="600" fontSize="sm">
              <HStack spacing={2}>
                <Icon as={LuUserPlus} boxSize={4} />
                <Text>Accès</Text>
              </HStack>
            </Tab>
            <Tab fontWeight="600" fontSize="sm">
              <HStack spacing={2}>
                <Icon as={LuMegaphone} boxSize={4} />
                <Text>Campagnes</Text>
              </HStack>
            </Tab>
            <Tab fontWeight="600" fontSize="sm">
              <HStack spacing={2}>
                <Icon as={LuEye} boxSize={4} />
                <Text>Visiteurs de cas</Text>
              </HStack>
            </Tab>
            <Tab fontWeight="600" fontSize="sm">
              <HStack spacing={2}>
                <Icon as={LuDownload} boxSize={4} />
                <Text>Export</Text>
              </HStack>
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pt={5}>
              <VStack spacing={5} align="stretch">

        {/* Search + Filters + Module legend in one card */}
        <Card borderRadius="xl" borderWidth="1px" borderColor="gray.200">
          <CardBody>
            <VStack spacing={4} align="stretch">
              {/* Search */}
              <InputGroup>
                <InputLeftElement><SearchIcon color="gray.400" /></InputLeftElement>
                <Input
                  placeholder="Rechercher par nom, email ou organisation..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  borderRadius="lg"
                  _focus={{ borderColor: 'purple.400', boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)' }}
                />
                {search && (
                  <InputRightElement>
                    <IconButton aria-label="Effacer" icon={<CloseIcon boxSize={2.5} />} size="sm" variant="ghost" onClick={() => setSearch('')} />
                  </InputRightElement>
                )}
              </InputGroup>

              {/* Filters row */}
              <Flex gap={3} align="center" wrap="wrap">
                <HStack spacing={1}>
                  <Icon as={LuFilter} color="gray.400" boxSize={4} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">Filtres :</Text>
                </HStack>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  maxW="180px"
                  size="sm"
                  borderRadius="lg"
                >
                  <option value="all">Tous les rôles</option>
                  <option value="user">Utilisateur</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </Select>
                <Select
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  maxW="180px"
                  size="sm"
                  borderRadius="lg"
                >
                  <option value="all">Tous les plans</option>
                  <option value="free">Sans abonnement</option>
                  <option value="pro">Essentiel</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Institution</option>
                  <option value="enterprise_pending">Institution (en attente)</option>
                </Select>
                {(roleFilter !== 'all' || planFilter !== 'all' || search) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="purple"
                    borderRadius="lg"
                    onClick={() => { setRoleFilter('all'); setPlanFilter('all'); setSearch('') }}
                  >
                    Réinitialiser
                  </Button>
                )}
                <Text fontSize="sm" color="gray.400" ml="auto">
                  {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''}
                </Text>
              </Flex>

              <Divider />

              {/* Module legend compact */}
              <HStack spacing={2} wrap="wrap">
                <Text fontSize="xs" color="gray.500" fontWeight="medium" mr={1}>Modules :</Text>
                {TOGGLE_COLUMNS.map((m) => (
                  <Tooltip key={m.key} label={m.description} hasArrow placement="top">
                    <Tag
                      size="sm"
                      colorScheme={m.colorScheme}
                      borderRadius="full"
                      cursor="help"
                      variant="subtle"
                    >
                      <Icon as={m.icon} mr={1} />
                      <TagLabel>{m.label}</TagLabel>
                    </Tag>
                  </Tooltip>
                ))}
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Users Table */}
        {filteredUsers.length === 0 ? (
          <Box textAlign="center" py={16} bg="white" borderRadius="xl" borderWidth="1px" borderColor="gray.200">
            <Icon as={LuUsers} boxSize={12} color="gray.300" />
            <Text color="gray.500" mt={3} fontWeight="medium">Aucun utilisateur trouvé</Text>
            <Text fontSize="sm" color="gray.400">Essayez de modifier vos filtres</Text>
          </Box>
        ) : (
          <Card borderRadius="xl" borderWidth="1px" borderColor="gray.200" overflow="hidden">
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr bg="gray.50">
                    <Th py={4} fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                      Utilisateur
                    </Th>
                    <Th fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                      Rôle
                    </Th>
                    <Th fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                      Plan
                    </Th>
                    {TOGGLE_COLUMNS.map((module) => (
                      <Th key={module.key} textAlign="center" minW="90px">
                        <Tooltip label={`${module.label} — ${module.description}`} placement="top" hasArrow>
                          <VStack spacing={1} cursor="help">
                            <Box
                              p={1.5}
                              borderRadius="md"
                              bg={`${module.colorScheme}.50`}
                              border="1px solid"
                              borderColor={`${module.colorScheme}.100`}
                            >
                              <Icon as={module.icon} boxSize={4} color={`${module.colorScheme}.600`} />
                            </Box>
                            <Text fontSize="9px" fontWeight="semibold" color="gray.500">
                              {module.label}
                            </Text>
                          </VStack>
                        </Tooltip>
                      </Th>
                    ))}
                    <Th textAlign="center" fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
                      Actions
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredUsers.map((user, idx) => (
                    <Tr
                      key={user.id}
                      bg={idx % 2 === 0 ? 'white' : 'gray.50'}
                      _hover={{ bg: 'purple.50', transition: 'all 0.15s' }}
                      transition="all 0.15s"
                    >
                      <Td py={3}>
                        <HStack spacing={3}>
                          <UserAvatar name={user.contactName} email={user.email} />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="semibold" fontSize="sm" color="gray.800">
                              {user.contactName || 'Sans nom'}
                            </Text>
                            <Text fontSize="xs" color="gray.500">{user.email}</Text>
                            {user.companyName && (
                              <HStack spacing={1} mt={0.5}>
                                <Icon as={LuBuilding2} boxSize={3} color="gray.400" />
                                <Text fontSize="xs" color="gray.400">{user.companyName}</Text>
                              </HStack>
                            )}
                          </VStack>
                        </HStack>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={roleConfig[user.role]?.colorScheme || 'gray'}
                          variant="subtle"
                          borderRadius="full"
                          px={2.5}
                          py={0.5}
                          fontSize="xs"
                          fontWeight="semibold"
                        >
                          {roleConfig[user.role]?.label || user.role}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={planColor(effectivePlan(user))}
                          variant="subtle"
                          borderRadius="full"
                          px={2.5}
                          py={0.5}
                          fontSize="xs"
                        >
                          {planLabel(effectivePlan(user))}
                        </Badge>
                      </Td>
                      {TOGGLE_COLUMNS.map((module) => {
                        const active = isColumnActive(user, module)
                        return (
                          <Td key={module.key} textAlign="center">
                            <Tooltip label={active ? `Désactiver ${module.label}` : `Activer ${module.label}`} placement="top" hasArrow>
                              <Box display="inline-block">
                                <Switch
                                  isChecked={active}
                                  onChange={() => module.kind === 'module'
                                    ? toggleModule(user.id, module.key, active)
                                    : toggleLabApp(user, module.key, active)}
                                  colorScheme={module.colorScheme}
                                  size="md"
                                />
                              </Box>
                            </Tooltip>
                          </Td>
                        )
                      })}
                      <Td textAlign="center">
                        <HStack spacing={1} justify="center">
                          <Tooltip label="Voir les détails" placement="top">
                            <IconButton
                              aria-label="Détails"
                              icon={<Icon as={LuEye} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="purple"
                              borderRadius="lg"
                              onClick={() => { setSelectedUser(user); onOpen() }}
                            />
                          </Tooltip>
                          <Tooltip label="Supprimer" placement="top">
                            <IconButton
                              aria-label="Supprimer"
                              icon={<Icon as={LuTrash2} />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              borderRadius="lg"
                              isDisabled={user.role === 'super_admin'}
                              onClick={() => { setUserToDelete(user); onDeleteOpen() }}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Card>
        )}
              </VStack>
            </TabPanel>
            <TabPanel px={0} pt={5}>
              {/* Two readings of the same activity, in the order they get used:
                  the site first (who is the pilot establishment, on which
                  tests), the operators underneath (who inside it actually
                  works). Neither is the sum of the other — a site's cases carry
                  its detection profile, whatever account filed them. */}
              <VStack spacing={6} align="stretch">
                <ChuActivity />
                {/* La comptabilité du flux vient après l'activité et avant les
                    opérateurs : elle répond à la question que l'activité fait
                    naître — sur ces volumes, qu'est-ce qui est réellement
                    entré dans l'évaluation, et qu'est-ce qui s'est perdu en
                    route. */}
                <BlotFlowAudit />
                <PilotsDashboard embedded />
              </VStack>
            </TabPanel>
            <TabPanel px={0} pt={5}>
              <InvitationsAdmin />
            </TabPanel>
            <TabPanel px={0} pt={5}>
              <CampaignsAdmin />
            </TabPanel>
            <TabPanel px={0} pt={5}>
              <CaseVisitorsAdmin />
            </TabPanel>
            <TabPanel px={0} pt={5}>
              <ExportAdmin />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      <UserDetailsModal
        user={selectedUser}
        isOpen={isOpen}
        onClose={onClose}
        onDisableModule={(userId, moduleType) => toggleModule(userId, moduleType, true)}
      />

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose} isCentered>
        <AlertDialogOverlay backdropFilter="blur(4px)">
          <AlertDialogContent borderRadius="xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" borderBottomWidth="1px" borderColor="gray.100">
              Supprimer l'utilisateur
            </AlertDialogHeader>
            <AlertDialogBody py={5}>
              <VStack align="start" spacing={3}>
                <Text fontSize="sm">Cette action est <strong>irréversible</strong>. Toutes les données associées seront supprimées.</Text>
                {userToDelete && (
                  <HStack bg="red.50" borderRadius="lg" p={3} w="100%" border="1px solid" borderColor="red.100" spacing={3}>
                    <UserAvatar name={userToDelete.contactName} email={userToDelete.email} />
                    <VStack align="start" spacing={0}>
                      <Text fontWeight="semibold" fontSize="sm">{userToDelete.contactName || 'Sans nom'}</Text>
                      <Text fontSize="xs" color="gray.500">{userToDelete.email}</Text>
                    </VStack>
                  </HStack>
                )}
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter borderTopWidth="1px" borderColor="gray.100">
              <Button ref={cancelRef} onClick={onDeleteClose} borderRadius="lg" size="sm">
                Annuler
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                ml={3}
                borderRadius="lg"
                size="sm"
                isLoading={isDeleting}
                loadingText="Suppression..."
              >
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}
